import { useEffect, useRef } from "react";
import { Preset_ScreenFull, Preset_WebcamFull, Preset_PresentationSlideWithWebcam } from "../presets/layoutPresets";
import {
  landscapePresets,
  portraitPresets,
  squarePresets,
} from "../presets/layoutPresets";
import logger from "../utils/logger";
import { resSize } from "../utils/resSize";
import type { RecordingQuality } from "../context/RecorderContext";

const useAutoLayoutPreset = ({
  screenStream,
  webcamStream,
  roundedWebcam,
  screenVideoRef,
  webcamVideoRef,
  canvasRatio,
  recordingQuality,
  applyPreset,
  setSelectedPreset,
  currentScenePreset,
  isSceneLoaded,
  isRecording,
  useSeparateStreamsMode,
}: {
  screenStream: MediaStream | null;
  webcamStream: MediaStream | null;
  screenVideoRef: React.RefObject<HTMLVideoElement>;
  webcamVideoRef: React.RefObject<HTMLVideoElement>;
  canvasRatio: string;
  recordingQuality: RecordingQuality;
  roundedWebcam: boolean;
  applyPreset: (
    presetFn: (
      w: number,
      h: number,
      ratio: number,
      roundedWebcam: boolean,
      canvasRatio: string
    ) => any,
    ratio: number,
    w: number,
    h: number,
    canvasRatio: string,
    roundedWebcam: boolean,
    preserveManualModifications?: boolean
  ) => void;
  setSelectedPreset: (key: string) => void;
  currentScenePreset?: string | null;
  isSceneLoaded?: boolean;
  isRecording?: boolean;
  useSeparateStreamsMode?: boolean;
}) => {
  // Track previous values to detect when only roundedWebcam changes
  const prevValuesRef = useRef({
    screenStream: null as MediaStream | null,
    webcamStream: null as MediaStream | null,
    roundedWebcam: false,
    canvasRatio: "",
    recordingQuality: "" as RecordingQuality,
  });
  
  // Track last applied preset to prevent duplicate applications
  const lastAppliedPresetRef = useRef<string>("");
  // Auto-apply preset when dependencies change
  useEffect(() => {
    // Skip auto-preset if scene hasn't loaded yet - wait for scene data
    // This prevents overriding scene-level preset before it's loaded from API
    if (isSceneLoaded === false) {
      return;
    }
    
    // Detect stream acquisition/stop events BEFORE the recording check
    const screenStreamJustAcquired = !!screenStream && !prevValuesRef.current.screenStream;
    const webcamStreamJustAcquired = !!webcamStream && !prevValuesRef.current.webcamStream;
    
    // Detect stream stop events (need these before the recording check)
    const screenStreamJustStopped = !screenStream && !!prevValuesRef.current.screenStream;
    const webcamStreamJustStopped = !webcamStream && !!prevValuesRef.current.webcamStream;
    
    // CRITICAL: Skip auto-preset during recording - user may be toggling streams
    // mid-recording and we don't want to override their selected layout
    // EXCEPTION: In separate streams mode, allow layout switch when a stream is acquired OR stopped
    // to ensure proper fallback layout when user toggles streams mid-recording
    if (isRecording) {
      const streamAcquired = screenStreamJustAcquired || webcamStreamJustAcquired;
      const streamStopped = screenStreamJustStopped || webcamStreamJustStopped;
      const shouldAllowLayoutSwitch = useSeparateStreamsMode && (streamAcquired || streamStopped);
      
      if (!shouldAllowLayoutSwitch) {
        // Still update ref to track current values for next comparison
        prevValuesRef.current = {
          screenStream,
          webcamStream,
          roundedWebcam,
          canvasRatio,
          recordingQuality,
        };
        return;
      }
      // If in separate streams mode and a stream state changed, continue to apply preset
      logger.info(`[AutoLayoutPreset] Allowing layout switch during recording: acquired(screen=${screenStreamJustAcquired}, webcam=${webcamStreamJustAcquired}), stopped(screen=${screenStreamJustStopped}, webcam=${webcamStreamJustStopped})`);
    }
    
    // Skip auto-preset if scene has a defined preset - preserve scene settings
    // EXCEPTION: Allow layout switch when screen share state changes (acquired or stopped)
    // This ensures proper layout transitions when user starts/stops screen sharing
    
    // Reset deduplication when a stream is stopped - allows preset to re-apply on restart
    if (screenStreamJustStopped || webcamStreamJustStopped) {
      lastAppliedPresetRef.current = "";
    }
    
    if (currentScenePreset && !screenStreamJustAcquired && !screenStreamJustStopped && !webcamStreamJustAcquired && !webcamStreamJustStopped) {
      return;
    }
    
    // Check if only roundedWebcam or recordingQuality changed
    const prevValues = prevValuesRef.current;
    const onlyRoundedWebcamChanged = 
      prevValues.screenStream === screenStream &&
      prevValues.webcamStream === webcamStream &&
      prevValues.canvasRatio === canvasRatio &&
      prevValues.recordingQuality === recordingQuality &&
      prevValues.roundedWebcam !== roundedWebcam;
    
    const onlyRecordingQualityChanged = 
      prevValues.screenStream === screenStream &&
      prevValues.webcamStream === webcamStream &&
      prevValues.canvasRatio === canvasRatio &&
      prevValues.roundedWebcam === roundedWebcam &&
      prevValues.recordingQuality !== recordingQuality;
    
    if (onlyRoundedWebcamChanged) {
      prevValuesRef.current = {
        screenStream,
        webcamStream,
        roundedWebcam,
        canvasRatio,
        recordingQuality,
      };
      return;
    }
    
    if (onlyRecordingQualityChanged) {
      prevValuesRef.current = {
        screenStream,
        webcamStream,
        roundedWebcam,
        canvasRatio,
        recordingQuality,
      };
      return;
    }
    
    // Update the ref with current values
    prevValuesRef.current = {
      screenStream,
      webcamStream,
      roundedWebcam,
      canvasRatio,
      recordingQuality,
    };
    
    const screenEl = screenVideoRef?.current;
    const webcamEl = webcamVideoRef?.current;
    const isReady = (video?: HTMLVideoElement | null) =>
      !!video && video.readyState >= 1 && video.videoWidth > 0;

    const bothStreamsExist = !!screenStream && !!webcamStream;

    // Calculate canvas dimensions using resSize utility
    // resSize now correctly returns {width, height} for all ratios including portrait
    const { width: canvasWidth, height: canvasHeight } = resSize(recordingQuality, canvasRatio);


    const applyAppropriatePreset = () => {
      const currentScreenEl = screenVideoRef?.current;
      const currentWebcamEl = webcamVideoRef?.current;
      const isScreenReady = isReady(currentScreenEl);
      const isWebcamReady = isReady(currentWebcamEl);

      // Helper to create deduplication signature
      const createPresetSignature = (presetKey: string, streams: string) => {
        return `${presetKey}-${canvasRatio}-${canvasWidth}x${canvasHeight}-${streams}`;
      };
      
      // Helper to apply preset with deduplication
      const applyPresetWithDedup = (
        presetFn: any,
        presetKey: string,
        ratio: number,
        streams: string
      ) => {
        const signature = createPresetSignature(presetKey, streams);
        if (lastAppliedPresetRef.current === signature) {
          return false;
        }
        lastAppliedPresetRef.current = signature;
        applyPreset(
          presetFn,
          ratio,
          canvasWidth,
          canvasHeight,
          canvasRatio,
          roundedWebcam,
          true
        );
        setSelectedPreset(presetKey);
        return true;
      };

      if (bothStreamsExist) {
        if (!isScreenReady || !isWebcamReady) {
          return;
        }

        const ratio = screenEl!.videoWidth / screenEl!.videoHeight;

        if (canvasRatio === "16:9") {
          const presetFn = landscapePresets.Preset_Default;
          applyPresetWithDedup(presetFn, "Preset_Default", ratio, "both");
          return;
        } else if (canvasRatio === "1:1") {
          const presetFn = squarePresets.Preset_CamBottomRight;
          applyPresetWithDedup(presetFn, "Preset_CamBottomRight", ratio, "both");
          return;
        } else if (canvasRatio === "9:16") {
          const presetFn = portraitPresets.Preset_SideCamStacked;
          applyPresetWithDedup(presetFn, "Preset_SideCamStacked", ratio, "both");
          return;
        }
        
        //logger.warn("❌ AutoLayoutPreset - No matching preset found for both streams");
        return;
      }

      if (!!screenStream && isScreenReady) {
        const ratio = screenEl!.videoWidth / screenEl!.videoHeight;
        applyPresetWithDedup(Preset_ScreenFull, "Preset_ScreenFull", ratio, "screen");
        return;
      }

      if (!!webcamStream && isWebcamReady) {
        const ratio = webcamEl!.videoWidth / webcamEl!.videoHeight;
        if (currentScenePreset === 'Preset_PresentationSlide' || currentScenePreset === 'Preset_PresentationSlideWithWebcam') {
          applyPresetWithDedup(Preset_PresentationSlideWithWebcam, "Preset_PresentationSlideWithWebcam", ratio, "webcam");
        } else {
          applyPresetWithDedup(Preset_WebcamFull, "Preset_WebcamFull", ratio, "webcam");
        }
        return;
      }

      //logger.warn("❌ No ready stream to apply preset");
    };

    const onScreenMeta = () => {
      if (screenEl && isReady(screenEl)) applyAppropriatePreset();
    };

    const onWebcamMeta = () => {
      if (webcamEl && isReady(webcamEl)) applyAppropriatePreset();
    };

    const screenReady = isReady(screenEl);
    const webcamReady = isReady(webcamEl);

    if (
      (bothStreamsExist && screenReady && webcamReady) ||
      (!bothStreamsExist && (screenReady || webcamReady))
    ) {
      applyAppropriatePreset();
    } else {
      if (screenStream && screenEl) {
        screenEl.addEventListener("loadedmetadata", onScreenMeta);
      }
      if (webcamStream && webcamEl) {
        webcamEl.addEventListener("loadedmetadata", onWebcamMeta);
      }

      logger.info('[AutoLayoutPreset] Waiting for metadata', {
        hasScreenStream: !!screenStream,
        hasScreenEl: !!screenEl,
        hasWebcamStream: !!webcamStream,
        hasWebcamEl: !!webcamEl,
        screenElReadyState: screenEl?.readyState,
        webcamElReadyState: webcamEl?.readyState,
        screenElVideoWidth: screenEl?.videoWidth,
        webcamElVideoWidth: webcamEl?.videoWidth,
      });

      return () => {
        if (screenEl)
          screenEl.removeEventListener("loadedmetadata", onScreenMeta);
        if (webcamEl)
          webcamEl.removeEventListener("loadedmetadata", onWebcamMeta);
      };
    }
  }, [
    screenStream,
    webcamStream,
    roundedWebcam,
    screenVideoRef,
    webcamVideoRef,
    canvasRatio,
    recordingQuality,
    applyPreset,
    setSelectedPreset,
    currentScenePreset,
    isSceneLoaded,
    isRecording,
    useSeparateStreamsMode,
  ]);
};

export default useAutoLayoutPreset;
